import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../../context/CartContext";
import { useAuth } from "../../api/hooks/useAuth";
import "./CartSidebar.css";

export default function CartSidebar() {
  const {
    cartItems,
    updateQuantity,
    removeFromCart,
    addToCart,
    getCartTotal,
    cartTotal,
    loading,
    fetchCart,
    isSidebarOpen,
    closeSidebar,
    getCartCount
  } = useCart();
  const { isAuthenticated } = useAuth();

  const navigate = useNavigate();
  const [updatingItemId, setUpdatingItemId] = useState(null);

  useEffect(() => {
    if (isSidebarOpen) {
      fetchCart();
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isSidebarOpen, fetchCart]);

  const removeItem = (id) => {
    removeFromCart(id);
  };

  const subtotal = cartTotal || getCartTotal();
  const freeShippingThreshold = 50;
  const isFreeShipping = subtotal >= freeShippingThreshold;
  const shipping = isFreeShipping ? 0 : 5.99;
  const amountAwayFromFreeShipping = Math.max(0, freeShippingThreshold - subtotal);

  const getDurationOptions = (item) => {
    const rawDurations = item.selectedVariant?.billing_durations || item.selectedVariant?.billingDurations || [];
    if (!Array.isArray(rawDurations) || rawDurations.length === 0) {
      return [];
    }
    return rawDurations
      .map((duration) => Number(duration))
      .filter((duration) => !Number.isNaN(duration));
  };

  const handlePurchaseTypeChange = async (item, selectedValue) => {
    if (!item || !selectedValue) return;
    const [selectedType, selectedDuration] = selectedValue.split('|');
    if (!selectedType) return;

    const duration = selectedType === 'subscription' ? Number(selectedDuration) : undefined;
    if (selectedType === item.purchaseType && (selectedType !== 'subscription' || Number(item.billingDuration) === duration)) {
      return;
    }

    const variantUuid = item.selectedVariant?.variant_uuid || item.selectedVariant?.uuid;
    if (!variantUuid) return;

    setUpdatingItemId(item.id);
    try {
      await removeFromCart(item.id, true, true);

      const payload = {
        item_uuid: item.productId,
        variant_uuid: variantUuid,
        quantity: item.quantity,
        purchase_type: selectedType,
      };

      if (selectedType === 'subscription' && duration && duration > 0) {
        payload.billing_duration = duration;
      }

      if (item.user_uuid) {
        payload.user_uuid = item.user_uuid;
      }
      if (item.ip_address) {
        payload.ip_address = item.ip_address;
      }

      await addToCart(payload);
    } catch (error) {
      console.error('Failed to update cart item purchase type:', error);
    } finally {
      setUpdatingItemId(null);
    }
  };

  const renderPurchaseTypeDropdown = (item) => {
    const durationOptions = getDurationOptions(item);
    const selectedValue = item.purchaseType === 'subscription'
      ? `subscription|${item.billingDuration || durationOptions[0] || 0}`
      : 'one_time|0';

    return (
      <div className="cart-sidebar-item-subscription">
        <select
          className="cart-sidebar-subscription-dropdown"
          value={selectedValue}
          onChange={(e) => handlePurchaseTypeChange(item, e.target.value)}
          disabled={updatingItemId === item.id}
        >
          <option value="one_time|0">One-time purchase</option>
          {durationOptions.length > 0 ? (
            durationOptions.map((duration) => (
              <option key={duration} value={`subscription|${duration}`}>
                {getDurationLabel(duration)} subscription with {item.discountPercentage}% discount
              </option>
            ))
          ) : item.purchaseType === 'subscription' ? (
            <option value={`subscription|${item.billingDuration}`}>
              {getDurationLabel(item.billingDuration)} subscription with {item.discountPercentage}% discount
            </option>
          ) : null}
        </select>
      </div>
    );
  };

  const total = cartTotal || (subtotal + shipping);

  // Format billing duration (e.g., 14 -> 2 weeks, 30 -> 1 month)
  const getDurationLabel = (days) => {
    if (!days) return "";
    if (days >= 30) {
      const months = Math.floor(days / 30);
      return `${months} month${months > 1 ? 's' : ''}`;
    }
    const weeks = Math.floor(days / 7);
    return `${weeks} week${weeks > 1 ? 's' : ''}`;
  };

  const handleCheckout = () => {
    closeSidebar();
    if (isAuthenticated) {
      navigate("/checkout");
    } else {
      // Redirect to login and then back to checkout
      navigate("/login", { state: { redirectTo: "/checkout" } });
    }
  };

  if (!isSidebarOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div className="cart-sidebar-overlay" onClick={closeSidebar}></div>
      
      {/* Sidebar */}
      <div className="cart-sidebar">
        {/* Header */}
        <div className="cart-sidebar-header">
          <h2 className="cart-sidebar-title">
            Cart • {getCartCount()}
          </h2>
          <button className="cart-sidebar-close" onClick={closeSidebar}>
            ×
          </button>
        </div>

        {/* Content */}
        <div className="cart-sidebar-content">
          {loading && cartItems.length === 0 ? (
            <div className="cart-sidebar-loading">
              <div className="loading-spinner"></div>
            </div>
          ) : cartItems.length === 0 ? (
            <div className="cart-sidebar-empty">
              <div className="empty-cart-icon">🛒</div>
              <h3>Your cart is empty</h3>
              <p>Browse our premium botanical collection.</p>
              <button onClick={closeSidebar} className="continue-shopping-btn">
                Continue Shopping
              </button>
            </div>
          ) : (
            <>
              {/* Free Shipping Progress Bar */}
              {!isFreeShipping && subtotal > 0 && (
                <div className="cart-sidebar-shipping-progress">
                  <div className="cart-sidebar-shipping-message">
                    You're ${amountAwayFromFreeShipping.toFixed(2)} away from free shipping!
                  </div>
                  <div className="cart-sidebar-progress-bar">
                    <div
                      className="cart-sidebar-progress-fill"
                      style={{ width: `${Math.min((subtotal / freeShippingThreshold) * 100, 100)}%` }}
                    />
                  </div>
                </div>
              )}

              {isFreeShipping && subtotal > 0 && (
                <div className="cart-sidebar-shipping-achieved">
                  🎉 You've qualified for free shipping!
                </div>
              )}

              {/* Cart Items */}
              <div className="cart-sidebar-items">
                {cartItems.map((item) => {
                  const packQuantity = item.selectedVariant?.attributes?.find(
                    (attr) => attr.attribute_type === "Pack Quantity"
                  )?.attribute_value || "";

                  const savings = item.originalPrice > item.price
                    ? (item.originalPrice - item.price) * item.quantity
                    : 0;

                  return (
                    <div key={item.id} className="cart-sidebar-item">
                      <img
                        src={item.image || '/placeholder-product.png'}
                        alt={item.name}
                        className="cart-sidebar-item-image"
                      />
                      
                      <div className="cart-sidebar-item-details">
                        <div className="cart-sidebar-item-header">
                          <h4 className="cart-sidebar-item-name">{item.name}</h4>
                          <button
                            className="cart-sidebar-item-delete"
                            onClick={() => removeItem(item.id)}
                            aria-label="Remove item"
                          >
                            🗑️
                          </button>
                        </div>
                        
                        {packQuantity && (
                          <p className="cart-sidebar-item-pack">
                            Choose a Pack Quantity: {packQuantity} Shots
                          </p>
                        )}
                        
                        {renderPurchaseTypeDropdown(item)}

                        <div className="cart-sidebar-item-controls-row">
                          <div className="cart-sidebar-item-quantity">
                            <button
                              className="cart-sidebar-quantity-btn"
                              onClick={() => {
                                const newQuantity = item.quantity - 1;
                                updateQuantity(item.id, newQuantity);
                              }}
                            >
                              −
                            </button>
                            <span className="cart-sidebar-quantity-value">{item.quantity}</span>
                            <button
                              className="cart-sidebar-quantity-btn"
                              onClick={() => updateQuantity(item.id, item.quantity + 1)}
                            >
                              +
                            </button>
                          </div>

                          <div className="cart-sidebar-item-pricing">
                            {item.originalPrice > item.price && (
                              <span className="cart-sidebar-item-original-price">
                                ${item.originalPrice.toFixed(2)}
                              </span>
                            )}
                            <div className="cart-sidebar-item-price-wrapper">
                              <span className="cart-sidebar-item-price">
                                ${item.price.toFixed(2)}
                              </span>
                              {item.multiplier > 1 && (
                                <span className="cart-sidebar-item-multiplier">
                                  x {item.multiplier}
                                </span>
                              )}
                            </div>
                            {savings > 0 && (
                              <span className="cart-sidebar-item-savings">
                                (Save ${savings.toFixed(2)})
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Footer with Checkout */}
        {cartItems.length > 0 && (
          <div className="cart-sidebar-footer">
            <button className="cart-sidebar-checkout-btn" onClick={handleCheckout}>
              Checkout • ${total.toFixed(2)}
            </button>
          </div>
        )}
      </div>
    </>
  );
}

